import * as React from 'react';
import type { AccountInfo } from '@azure/msal-browser';
import {
  ActionButton, DefaultButton, MessageBar, MessageBarType, Panel, PrimaryButton, SearchBox, Slider, Spinner, Toggle
} from '@fluentui/react';
import SharePointOrganizer from '../../src/webparts/sharePointOrganizer/components/SharePointOrganizer';
import { GraphService, type ISiteInfo } from '../../src/services/GraphService';
import { DEFAULT_RULE_OPTIONS, type IRuleOptions } from '../../src/models/Issue';
import { describeGraphError } from '../../src/utils/errors';
import type { ITelemetry } from '../../src/utils/telemetry';
import { BASE_SCOPES, CLIENT_ID, ENVIRONMENT_LABEL, MANAGE_SCOPES } from './config';
import { signIn, signOut } from './auth';
import { createGraphClient } from './graph';
import { GraphAuditLog } from './GraphAuditLog';
import { AiClient, fetchCredits } from './aiClient';
import { GraphLibraryMetadata } from './GraphLibraryMetadata';

/** Browser console until Application Insights is wired up with the backend. */
const telemetry: ITelemetry = {
  event: (name, data) => console.info('[SharePointOrganizer]', name, data ?? ''),
  error: (name, error, data) => console.error('[SharePointOrganizer]', name, data ?? '', error)
};

interface ISiteSettings {
  allowChanges: boolean;
  maxDepth: number;
}

const DEFAULT_SETTINGS: ISiteSettings = { allowChanges: false, maxDepth: DEFAULT_RULE_OPTIONS.maxDepth };

/** Per-site settings kept in this browser. Moving them to the backend comes with tenant settings. */
function loadSettings(siteId: string): ISiteSettings {
  try {
    const raw: string | null = localStorage.getItem(`spo.settings.${siteId}`);
    return raw ? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function saveSettings(siteId: string, settings: ISiteSettings): void {
  try {
    localStorage.setItem(`spo.settings.${siteId}`, JSON.stringify(settings));
  } catch {
    // Settings still apply for this visit.
  }
}

function loadLastSite(): ISiteInfo | undefined {
  try {
    const raw: string | null = localStorage.getItem('spo.lastSite');
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}

const FOOTER_LINKS: { href: string; text: string }[] = [
  { href: '/security', text: 'Security' },
  { href: '/privacy', text: 'Privacy' },
  { href: '/terms', text: 'Terms' },
  { href: '/support', text: 'Support' }
];

function Footer(): React.ReactElement {
  return (
    <footer className="footer">
      <span>© {new Date().getFullYear()} EightyThreeLabs Inc.</span>
      <nav>{FOOTER_LINKS.map(l => <a key={l.href} href={l.href}>{l.text}</a>)}</nav>
    </footer>
  );
}

const CHECKS: [string, string][] = [
  ['Duplicate files', 'The same document saved in several places — which copy is the real one?'],
  ['Unclear names', '"Document1", "final FINAL v2", "Scan0042": names nobody can search for.'],
  ['Empty and look-alike folders', '"HR", "Human Resources" and "HR Docs" side by side, and folders with nothing in them.'],
  ['Deep nesting and long paths', 'Folders ten levels down, and paths near SharePoint\'s 400-character limit.'],
  ['Catch-all folders', '"Misc", "Old", "New folder (3)" — where files go to be forgotten.'],
  ['No structure', 'Everything at the top level? Get a proposal with a handful of clear categories.']
];

const STEPS: [string, string][] = [
  ['Scan', 'Read-only. Nothing changes.'],
  ['Understand', 'A health report in plain language.'],
  ['Review', 'Every suggestion explains why. Approve, edit or skip.'],
  ['Apply safely', 'Dry run first, then every change is logged — and undoable.']
];

const FAQ: [string, string][] = [
  ['Does it delete anything?', 'No. Items you remove go to a quarantine folder in the same library, and every change can be undone.'],
  ['Where do my documents go?', 'Nowhere. They stay in your Microsoft 365; the app works on them where they are.'],
  ['What does the AI see?', 'Only when you ask for AI suggestions: a file\'s name, folder and the first 2,000 characters of its text. Nothing is stored or used for training.'],
  ['Do I need an IT person?', 'No. Sign in with your Microsoft account. The first time, your admin may need to click "Approve".'],
  ['Does it work with Teams?', 'Yes — Teams files live in SharePoint. Folders that belong to Teams channels are never moved.'],
  ['Does it replace Microsoft\'s admin tools?', 'It works alongside them. Microsoft\'s tools govern who can access what; SharePoint Organizer fixes what\'s inside your libraries.']
];

function SignInPage(): React.ReactElement {
  const signInButton = (
    <PrimaryButton text="Sign in with Microsoft" iconProps={{ iconName: 'Signin' }} onClick={() => { void signIn(); }} />
  );
  return (
    <div className="home">
      <header className="topbar">
        <div className="brand">
          <img src="/favicon.svg" alt="" width={24} height={24} />
          <span>SharePoint Organizer</span>
          {ENVIRONMENT_LABEL && <span className="env-badge">{ENVIRONMENT_LABEL}</span>}
        </div>
        {signInButton}
      </header>

      <section className="hero">
        <p className="eyebrow">Get your SharePoint ready for Copilot</p>
        <h1>Scan your SharePoint. Understand the mess. Fix it safely.</h1>
        <p className="lead">
          SharePoint Organizer finds duplicates, unclear names and folder clutter, recommends a clean structure,
          and applies only the changes you approve. Nothing is deleted, and everything can be undone.
        </p>
        <div className="hero-actions">
          {signInButton}
          <span className="muted">Free to try — scanning is free, and 100 AI credits are included.</span>
        </div>
      </section>

      <section className="band">
        <h2>How it works</h2>
        <ol className="steps">
          {STEPS.map(([title, text], i) => (
            <li key={title}><span className="step-number">{i + 1}</span><strong>{title}</strong><span>{text}</span></li>
          ))}
        </ol>
      </section>

      <section className="band">
        <h2>What it checks</h2>
        <div className="grid">
          {CHECKS.map(([title, text]) => <div key={title} className="tile"><h3>{title}</h3><p>{text}</p></div>)}
        </div>
      </section>

      <section className="band">
        <h2>Safe by design</h2>
        <div className="grid">
          <div className="tile"><h3>Nothing is deleted</h3><p>Unwanted items go to a quarantine folder you can check and restore.</p></div>
          <div className="tile"><h3>You approve every change</h3><p>See the library before and after. A dry run checks everything before a single file moves.</p></div>
          <div className="tile"><h3>Logged and undoable</h3><p>An audit log in your own site records every change, and History undoes any batch.</p></div>
          <div className="tile"><h3>Your files stay put</h3><p>Your documents stay in your Microsoft 365. The app can only reach what you already can.</p></div>
        </div>
        <p><a href="/security">How we keep your files safe →</a></p>
      </section>

      <section className="band">
        <h2>Questions</h2>
        <dl className="faq">
          {FAQ.map(([q, a]) => <React.Fragment key={q}><dt>{q}</dt><dd>{a}</dd></React.Fragment>)}
        </dl>
      </section>

      <section className="band cta">
        <h2>Try it on one library</h2>
        <p className="muted">Scanning changes nothing. See what's in there first.</p>
        {signInButton}
      </section>

      <Footer />
    </div>
  );
}

function SitePicker(props: { graph: GraphService; onPick: (site: ISiteInfo) => void }): React.ReactElement {
  const { graph, onPick } = props;
  const [query, setQuery] = React.useState<string>('');
  const [sites, setSites] = React.useState<ISiteInfo[] | undefined>(undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);

  const search = React.useCallback((text: string): void => {
    setSites(undefined);
    setError(undefined);
    graph.searchSites(text)
      .then(setSites)
      .catch(e => { telemetry.error('sites.failed', e); setError(describeGraphError(e)); });
  }, [graph]);

  React.useEffect(() => { search(''); }, [search]);

  return (
    <section className="picker">
      <h2>Choose a SharePoint site</h2>
      <p className="muted">Pick the site whose document library you want to organize.</p>
      <SearchBox
        placeholder="Search sites by name"
        value={query}
        onChange={(_, v) => setQuery(v ?? '')}
        onSearch={search}
        onClear={() => search('')}
      />
      {error && <MessageBar messageBarType={MessageBarType.error} isMultiline>{error}</MessageBar>}
      {!sites && !error && <Spinner label="Finding sites…" />}
      {sites && sites.length === 0 && <MessageBar>No sites found. Try a different name.</MessageBar>}
      {sites && sites.length > 0 && (
        <ul className="site-list">
          {sites.map(site => (
            <li key={site.id}>
              <button type="button" onClick={() => onPick(site)}>
                <span className="site-name">{site.displayName}</span>
                <span className="site-url">{site.webUrl}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function App(props: { account: AccountInfo | undefined }): React.ReactElement {
  const { account } = props;
  const [site, setSite] = React.useState<ISiteInfo | undefined>(loadLastSite);
  const [settings, setSettings] = React.useState<ISiteSettings>(() => (site ? loadSettings(site.id) : DEFAULT_SETTINGS));
  const [settingsOpen, setSettingsOpen] = React.useState<boolean>(false);

  const readClient = React.useMemo(() => createGraphClient(BASE_SCOPES), []);
  const manageClient = React.useMemo(() => createGraphClient(MANAGE_SCOPES), []);
  const graph: GraphService = React.useMemo(() => new GraphService(readClient), [readClient]);
  const auditLog: GraphAuditLog | undefined = React.useMemo(
    () => (site ? new GraphAuditLog(readClient, manageClient, site.id, site.webUrl) : undefined),
    [readClient, manageClient, site]
  );
  const [credits, setCredits] = React.useState<number | undefined>(undefined);
  const [usOnly, setUsOnly] = React.useState<boolean>(false);
  const ai: AiClient = React.useMemo(() => new AiClient(readClient, setCredits), [readClient]);
  const tagging = React.useMemo(() => (site ? {
    ai,
    createMetadata: (driveId: string) => new GraphLibraryMetadata(readClient, manageClient, site.id, driveId)
  } : undefined), [ai, readClient, manageClient, site]);
  React.useEffect(() => {
    if (account && CLIENT_ID) {
      fetchCredits().then(c => { setCredits(c.balance); setUsOnly(c.dataResidency === 'us'); }).catch(e => telemetry.error('credits.failed', e));
    }
  }, [account]);
  const ruleOptions: IRuleOptions = React.useMemo(() => ({ ...DEFAULT_RULE_OPTIONS, maxDepth: settings.maxDepth }), [settings.maxDepth]);

  if (!CLIENT_ID) {
    return (
      <main className="landing">
        <MessageBar messageBarType={MessageBarType.warning} isMultiline>
          The app isn't configured yet: set <code>VITE_CLIENT_ID</code> in <code>web/.env</code> to the client ID of the
          SharePoint Organizer app registration.
        </MessageBar>
      </main>
    );
  }
  if (!account) {
    return <SignInPage />;
  }

  const pickSite = (picked: ISiteInfo | undefined): void => {
    setSite(picked);
    setSettings(picked ? loadSettings(picked.id) : DEFAULT_SETTINGS);
    try {
      if (picked) {
        localStorage.setItem('spo.lastSite', JSON.stringify(picked));
      } else {
        localStorage.removeItem('spo.lastSite');
      }
    } catch {
      // Not remembered next time; nothing else depends on it.
    }
  };

  const updateSettings = (next: ISiteSettings): void => {
    setSettings(next);
    if (site) {
      saveSettings(site.id, next);
    }
  };

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand">
          <img src="/favicon.svg" alt="" width={24} height={24} />
          <span>SharePoint Organizer</span>
          {ENVIRONMENT_LABEL && <span className="env-badge">{ENVIRONMENT_LABEL}</span>}
        </div>
        <div className="topbar-actions">
          {site && <ActionButton iconProps={{ iconName: 'Switch' }} text="Change site" onClick={() => pickSite(undefined)} />}
          {site && <ActionButton iconProps={{ iconName: 'Settings' }} text="Settings" onClick={() => setSettingsOpen(true)} />}
          {credits !== undefined && (
            <span className="credits" title={`AI credits left for your organization. AI processing: ${usOnly ? 'United States only' : 'Microsoft Azure, any region'}.`}>
              {credits.toLocaleString()} AI credits{usOnly && ' · US only'}
            </span>
          )}
          <span className="user">{account.username}</span>
          <DefaultButton text="Sign out" onClick={() => { void signOut(); }} />
        </div>
      </header>

      <main className="content">
        {!site && <SitePicker graph={graph} onPick={pickSite} />}
        {site && auditLog && (
          <SharePointOrganizer
            key={site.id}
            graphService={graph}
            siteId={site.id}
            siteTitle={site.displayName}
            ruleOptions={ruleOptions}
            auditLog={auditLog}
            userName={account.username}
            allowChanges={settings.allowChanges}
            telemetry={telemetry}
            ai={ai}
            tagging={tagging}
          />
        )}
      </main>

      <Panel isOpen={settingsOpen} onDismiss={() => setSettingsOpen(false)} headerText="Settings" isLightDismiss>
        <p className="muted">For {site?.displayName}. Saved in this browser.</p>
        <h3>Health rules</h3>
        <Slider
          label="Flag folders nested deeper than"
          min={2}
          max={15}
          value={settings.maxDepth}
          onChange={v => updateSettings({ ...settings, maxDepth: v })}
        />
        <h3>Safety</h3>
        <Toggle
          label="Allow changes to this site"
          onText="On: approved changes can be applied"
          offText="Off: recommend only (nothing is changed)"
          checked={settings.allowChanges}
          onChange={(_, checked) => updateSettings({ ...settings, allowChanges: !!checked })}
        />
      </Panel>
      <Footer />
    </div>
  );
}
